import React, { FC, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import {
  dealsLinks,
  bossModeDealsLinks,
  newsLink,
  toLandingLinks,
} from 'components/ResolveHeader/utils';
import LogoutIcon from 'icons/Logout.icon';
import PlusIcon from 'icons/Plus.icon';
import ProfileIcon from 'icons/Profile.icon';
import WorldIcon from 'icons/World.icon';
import { Button } from 'ncoded-component-library';
import AuthContext from 'providers/Auth/Auth.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { getResolveUrl, getUserFullName } from 'utils';
import useMatchMedia from 'hooks/useMatchMedia';
import AddProfile from 'icons/AddProfile.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import BoltIcon from 'icons/Bolt.icon';
import NewsIcon from 'icons/News.icon';
import OfficebuildingIcon from 'icons/Officebuilding.icon';
import AddressBookIcon from 'icons/AddressBook.icon';
import {
  BUSINESS_ROUTE,
  URGENT_ROUTE,
  CONTACTS_ROUTE,
  CREATE_DEAL_ROUTE,
  ADMIN_DASHBOARD_ROUTE,
  SIGNUP_ROUTE,
  LOGIN_ROUTE,
} from 'data/global';
import ReportsIcon from 'icons/Reports.icon';
import CreateDataRoomContext from 'providers/CreateDataRoom/CreateDataRoom.context';
import UserEnterpriseLogo from '../UserEnterpriseLogo';
import useUserEnterpriseRoute from 'hooks/useUserEnterpriseRoute';
import GroupUsersIcon from 'icons/GroupUsers.icon';

import './NavigationList.styles.scss';
import './NavigationList.styles.responsive.scss';

type NavigationListProps = {
  onClose?: () => void;
};

const NavigationList: FC<NavigationListProps> = ({ children, onClose }) => {
  const { isLoggedIn, logoutRequest } = useContext(AuthContext);
  const {
    currentUser,
    isSuperAdmin,
    isOfficeManager,
    isRegularEnterpriseMember,
    isRWCMember,
    updatePreferences,
  } = useContext(CurrentUserContext);
  const { openCreateDataRoomModal } = useContext(CreateDataRoomContext);

  const isTablet = useMatchMedia('isTablet');

  const { t } = useTranslation();

  const enterpriseRoute = useUserEnterpriseRoute();

  const { seenEnterpriseOverview } = currentUser || {};

  const markEnterpriseOverviewSeen = useCallback(() => {
    if (seenEnterpriseOverview) return;
    updatePreferences({ seenEnterpriseOverview: true });
  }, [seenEnterpriseOverview, updatePreferences]);

  const dealNavigationList = useMemo(
    () => (
      <ul>
        <li>
          <NavLink
            to="/dash/profile"
            className={({ isActive }) => {
              if (isActive) return 'active';
            }}
          >
            <ProfileIcon strokeWidth={1.5} />
            {t('profile')}
          </NavLink>
        </li>
        <li>
          <NavLink
            to={CREATE_DEAL_ROUTE}
            className={({ isActive }) => {
              if (isActive) return 'active';
            }}
          >
            <PlusIcon />
            {t('newDeal')}
          </NavLink>
        </li>

        {isRWCMember && (
          <li>
            <Button
              variant="link"
              onClick={() => {
                onClose?.();
                openCreateDataRoomModal();
              }}
            >
              <PlusIcon strokeWidth={1.5} />
              {t('newDataRoom')}
            </Button>
          </li>
        )}

        {(isSuperAdmin ? bossModeDealsLinks : dealsLinks).map(
          ({ href, label, icon: Icon }) => (
            <li key={href}>
              <NavLink
                to={href}
                className={({ isActive }) => {
                  if (isActive) return 'active';
                }}
              >
                {Icon && <Icon strokeWidth={1.6} />}
                {t(label)}
              </NavLink>
            </li>
          ),
        )}

        {enterpriseRoute && (
          <li>
            <NavLink
              to={enterpriseRoute}
              onClick={markEnterpriseOverviewSeen}
              className={({ isActive }) =>
                classNames({
                  active: isActive,
                })
              }
            >
              {isRWCMember || !isOfficeManager ? (
                <ReportsIcon strokeWidth={1.5} />
              ) : (
                <GroupUsersIcon strokeWidth={1.5} />
              )}
              {isRegularEnterpriseMember
                ? t(isRWCMember ? 'suggestedActionsLc' : 'overview')
                : isRWCMember
                ? 'RWC'
                : t(isOfficeManager ? 'team' : 'analytics')}
            </NavLink>
          </li>
        )}

        <li>
          <NavLink
            to={CONTACTS_ROUTE}
            className={({ isActive }) => {
              if (isActive) return 'active';
            }}
          >
            <AddressBookIcon />
            {t('contacts')}
          </NavLink>
        </li>
      </ul>
    ),
    [
      isRWCMember,
      isSuperAdmin,
      isOfficeManager,
      isRegularEnterpriseMember,
      enterpriseRoute,
      markEnterpriseOverviewSeen,
      t,
      onClose,
      openCreateDataRoomModal,
    ],
  );

  const bossModeNavigationList = useMemo(
    () => (
      <ul>
        <li>
          <NavLink
            to={ADMIN_DASHBOARD_ROUTE}
            className={({ isActive }) => {
              if (isActive) return 'active';
            }}
          >
            <WorldIcon />
            {t('adminDashboard')}
          </NavLink>
        </li>
      </ul>
    ),
    [t],
  );

  const logoutNavigationList = useMemo(
    () => (
      <ul>
        <li>
          <Button
            variant="link"
            icon={LogoutIcon}
            onClick={() => logoutRequest()}
          >
            {t('logout')}
          </Button>
        </li>
      </ul>
    ),
    [logoutRequest, t],
  );

  const loggedOutNavigationList = useMemo(
    () => (
      <ul>
        <li>
          <NavLink to={SIGNUP_ROUTE}>
            <AddProfile />
            {t('join')}
          </NavLink>
        </li>
        <li>
          <NavLink to={LOGIN_ROUTE}>
            <LockclosedIcon /> {t('signIn')}
          </NavLink>
        </li>
      </ul>
    ),
    [t],
  );

  return (
    <div className="navigation-list">
      {isLoggedIn ? (
        <div className="navigation-list__user">
          <p className="navigation-list__user__name">
            {getUserFullName(currentUser.firstName, currentUser.lastName)}
          </p>

          {isTablet && <UserEnterpriseLogo />}
        </div>
      ) : (
        <ul className="navigation-list__main-nav">
          {toLandingLinks.map(({ href, label }) => (
            <li key={href}>
              <a href={getResolveUrl(href)} aria-label={t(label)}>
                {t(label)}
              </a>
            </li>
          ))}
        </ul>
      )}

      <div className="navigation-list__navigation">
        {children || (
          <>
            {isLoggedIn && dealNavigationList}

            {isTablet && (
              <ul>
                <li>
                  <NavLink
                    to={BUSINESS_ROUTE}
                    className={({ isActive }) => {
                      if (isActive) return 'active';
                    }}
                  >
                    <OfficebuildingIcon />
                    {t('goingConcernListings')}
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={URGENT_ROUTE}
                    className={({ isActive }) => {
                      if (isActive) return 'active';
                    }}
                  >
                    <BoltIcon />
                    {t('urgentSaleListings')}
                  </NavLink>
                </li>
                <li>
                  <a href={getResolveUrl(newsLink.href)}>
                    <NewsIcon />
                    {t(newsLink.label)}
                  </a>
                </li>
              </ul>
            )}

            {isSuperAdmin && bossModeNavigationList}

            {isLoggedIn ? logoutNavigationList : loggedOutNavigationList}
          </>
        )}
      </div>
    </div>
  );
};

export default NavigationList;
